import { DateTime } from 'luxon';
import { AboSubscriptionOrderProduct } from 'src/adminPanel/entities/office/subscriptions/AboSubscriptionOrderProduct';
import { fromJsonArray, fromJsonArrayWith } from 'src/core/utils';
import BaseEntity from 'src/entities/BaseEntity';
import CalculatedVolumeDiscount from 'src/entities/cart/response/CalculatedVolumeDiscount';
import { getTotalQuantity } from 'src/entities/cart/response/OrderProductListGrouped';
import ProductGroup from 'src/entities/product-group/ProductGroup';
import CustomField from 'src/entities/products/CustomField';
import VolumeDiscount from 'src/entities/volume-discount/VolumeDiscount';
import { EntityDescriptions } from 'src/store/entities/types';
import EntityDescription from 'src/store/entities/types/EntityDescription';
import { ProductProperties } from './ProductProperty';
import { productText } from './productTexts';
import { UsedUnitOffer, usedUnitOfferFromJson } from './UsedUnitOffer';

export const NEW_SERVICE_PRODUCT_ID: string = '1999110';
export const ABO_PRODUCT_ID: string = '7115100';
export const ABO_PRODUCT_TYPE_ID: string = '7115110';
export const ABO_PRODUCT_COLOR_ID: string = '7115120';
export const ABO_PRODUCT_CAPSULES_ID: string = '7115130';

export type ProductKind = 'set' | 'variable' | 'leaf';

interface ProductInfo {
    pricePerMonth?: number;
    priceOfTotalRuntime?: number;
    specialDeliveryInfo?: string;
    currencyIso?: string;
}

interface AboProductInfo {
    monthlyCredit?: number;
    monthlyService?: number;
    monthlyTotal?: number;
    currencyIso?: string;
}

interface ServiceProductInfo {
    benefitDescaler?: number;
    benefitCredit?: number;
    benefitAppliance?: number;
    benefitTotal?: number;
    currencyIso?: string;
}

export const DIVERS_ARTICLE_GROUP = 'ARTICLE_GROUP_1999';
export const DISCRIMINATOR = ['SINGLE', 'VARIANT', 'SET'] as const;

export type Discriminator = typeof DISCRIMINATOR[number];

export interface CalculatedPriceAndDiscount {
    price: number;
    discount: number;
    volumeDiscount?: CalculatedVolumeDiscount;
    pricePerMonth: number;
    priceOfTotalRuntime: number;
    currencyIso?: string;
}

export default class Product extends BaseEntity {
    readonly articleNumber: string;
    readonly articleGroupNormalizedName: string;
    readonly localizedName: string;
    readonly localizedDescription: string;
    /** 12.8: the text blocks of name and description (TPL_PRODUCT_NAME_/DESC_<number>); undefined on older servers. */
    readonly nameKey?: string;
    readonly descriptionKey?: string;
    readonly imageUrl?: string;
    readonly thumbnailUrl?: string;
    readonly imageUrls: string[];
    readonly netPrice: number;
    readonly grossPrice: number;
    readonly netDiscount: number;
    readonly grossDiscount: number;
    readonly taxRate: number;
    readonly currencyIso: string;
    readonly colli: number;
    readonly colliEntity: string;
    readonly variants: Product[];
    readonly setProducts: Product[];
    readonly productGroups: ProductGroup[];
    readonly quantity: number;
    readonly minQuantity: number;
    readonly maxQuantity: number;
    readonly outOfStock: boolean;
    /** R25-5: warehouse sum above zero (display); undefined on older servers. */
    readonly inStock?: boolean;
    /** R27-40: IN_PRODUCTION | IN_STOCK | RESERVED | OUT_OF_STOCK; undefined = non-deliverable, or LR13: no offer right. */
    readonly stockStatus?: 'IN_PRODUCTION' | 'IN_STOCK' | 'RESERVED' | 'OUT_OF_STOCK';
    /** R27-42: "Auslieferbarer Artikel" of the effective settings; false = never show a stock status. */
    readonly deliverable?: boolean;
    readonly orderDirectly: boolean;
    readonly specialOffer: boolean;
    readonly alreadyOrdered: boolean;
    readonly mutuallyExclusiveSelectable: boolean;
    readonly variantLabelType: string;
    readonly productProperties: ProductProperties;
    readonly productInfo?: ProductInfo;
    readonly aboProductInfo?: AboProductInfo;
    readonly serviceProductInfo?: ServiceProductInfo;
    readonly customFields: CustomField[];
    readonly isCustomFlyer: boolean;
    /** R56-7: the visitor may send an inquiry („Anfrage schicken"); undefined = not restricted. */
    readonly canInquire?: boolean;
    /** R56-7: the visitor may configure and order („Produkt konfigurieren"); undefined = not restricted. */
    readonly canConfigure?: boolean;
    readonly allInfo: boolean;
    readonly discriminator: Discriminator;
    readonly vendorId: number;
    /** R25-6: the used machines offered for this article (AVAILABLE pieces, cheapest first). */
    readonly usedUnits: UsedUnitOffer[];
    readonly usedFromPriceNet?: number;
    readonly usedFromPriceGross?: number;
    /** R49-10: PRODUCT_SPECIAL | GROUP_SPECIAL — the customer's Sonderpreis is the price, no Staffel applies. */
    readonly specialPriceSource?: string;
    /** R49-10: the list price the Sonderpreis replaced, net and gross. */
    readonly listNetPrice?: number;
    readonly listGrossPrice?: number;

    constructor(json: any) {
        super({ id: json.vendorId != null ? `${json.articleNumber}_${json.vendorId}` : json.articleNumber, ...json });

        this.articleNumber = json.articleNumber;
        this.articleGroupNormalizedName = json.articleGroupNormalizedName;
        this.nameKey = json.nameKey ?? undefined;
        this.descriptionKey = json.descriptionKey ?? undefined;
        // 12.10: never a raw key on screen; 12.8: registered for the translator mode
        this.localizedName = productText(json.localizedName, this.nameKey);
        this.localizedDescription = productText(json.localizedDescription, this.descriptionKey);
        this.imageUrl = json.imageUrl;
        this.thumbnailUrl = json.thumbnailUrl ?? json.imageUrl;
        this.imageUrls = json.imageUrls ?? [];
        this.netPrice = json.price;
        this.grossPrice = json.grossPrice;
        this.netDiscount = this.netPrice < 0 ? Math.abs(this.netPrice) : json.discount ?? 0;
        this.grossDiscount = this.grossPrice < 0 ? Math.abs(this.grossPrice) : json.grossDiscount ?? 0;
        this.taxRate = json.taxRate;
        this.currencyIso = json.currencyIso4217 || json.currencyIso;
        this.colli = json.colli;
        this.colliEntity = json.colliEntity;
        this.variants = fromJsonArray(Product, json.variants);
        this.setProducts = fromJsonArray(Product, json.setProducts);
        this.productGroups = json.productGroups ? json.productGroups.map((g: any) => new ProductGroup(g)) : [];
        this.quantity = json.quantity;
        this.minQuantity = json.minQuantity ?? json.quantity;
        this.maxQuantity = json.maxQuantity ?? json.quantity;
        this.outOfStock = json.outOfStock;
        this.inStock = json.inStock;
        this.stockStatus = json.stockStatus ?? undefined;
        this.deliverable = json.deliverable ?? undefined;
        this.orderDirectly = json.orderDirectly ?? false;
        this.specialOffer = json.specialOffer ?? false;
        this.alreadyOrdered = json.alreadyOrdered ?? false;
        this.mutuallyExclusiveSelectable = json.mutuallyExclusiveSelectable ?? false;
        this.variantLabelType = json.variantLabelType ?? 'quantity';
        this.productProperties = new ProductProperties(json.productProperties);
        this.productInfo = json.productInfo;
        this.aboProductInfo = json.aboProductInfo;
        this.serviceProductInfo = json.serviceProductInfo;
        this.customFields = fromJsonArrayWith(CustomField.fromJson, json.customFields);
        this.isCustomFlyer = json.isCustomFlyer ?? false;
        this.canInquire = json.canInquire ?? undefined;
        this.canConfigure = json.canConfigure ?? undefined;
        this.allInfo = json.allInfo ?? false;
        this.discriminator = json.discriminator;
        this.vendorId = json.vendorId;
        this.usedUnits = Array.isArray(json.usedUnits) ? json.usedUnits.map(usedUnitOfferFromJson) : [];
        this.usedFromPriceNet = json.usedFromPriceNet ?? undefined;
        this.usedFromPriceGross = json.usedFromPriceGross ?? undefined;
        this.specialPriceSource = json.specialPriceSource ?? undefined;
        this.listNetPrice = json.listPrice ?? undefined;
        this.listGrossPrice = json.listGrossPrice ?? undefined;
    }

    /** "Gebraucht ab" in the visitor's price display; undefined when no used piece is offered. */
    public get usedFromPrice(): number | undefined {
        if (this.usedUnits.length === 0) return undefined;
        return sessionStorage.getItem('priceType') === 'net'
            ? this.usedFromPriceNet ?? this.usedUnits[0].priceNet
            : this.usedFromPriceGross ?? this.usedUnits[0].priceGross;
    }

    public findUsedUnit(id: string | null | undefined): UsedUnitOffer | undefined {
        if (!id) return undefined;
        return this.usedUnits.find(unit => unit.id === id);
    }

    public get price(): number {
        if (sessionStorage.getItem('priceType') === 'gross') {
            return this.grossPrice;
        } else if (sessionStorage.getItem('priceType') === 'net') {
            return this.netPrice;
        } else {
            return this.grossPrice ? this.grossPrice : this.netPrice;
        }
    }

    public get discount(): number {
        if (sessionStorage.getItem('priceType') === 'gross') {
            return this.grossDiscount;
        } else if (sessionStorage.getItem('priceType') === 'net') {
            return this.netDiscount;
        } else {
            return this.grossDiscount ? this.grossDiscount : this.netDiscount;
        }
    }

    public get allImages(): string[] {
        return [this.imageUrl, ...this.imageUrls].filter((x): x is string => x != null);
    }

    isVariableProduct(): boolean {
        return this.discriminator === 'VARIANT';
    }

    isNewServiceProduct(): boolean {
        return this.articleNumber === NEW_SERVICE_PRODUCT_ID;
    }

    isDiversProduct(): boolean {
        return this.articleGroupNormalizedName === DIVERS_ARTICLE_GROUP;
    }

    isMutuallyExcluseveSelectable(): boolean {
        return (
            this.mutuallyExclusiveSelectable || this.isNewServiceProduct() || this.articleNumber === ABO_PRODUCT_TYPE_ID
        );
    }

    isSetProduct(): boolean {
        return this.discriminator === 'SET';
    }

    isAboProduct(): boolean {
        return this.articleNumber === ABO_PRODUCT_ID;
    }

    get productKind(): ProductKind {
        if (this.isSetProduct()) return 'set';
        if (this.isVariableProduct()) return 'variable';
        return 'leaf';
    }

    hasCustomFields(): boolean {
        return this.customFields.length > 0;
    }

    hasVisibleCustomFields(): boolean {
        return this.customFields.filter(f => !f.hidden).length > 0;
    }

    /**
     * Recursively searches a product by it's id within nested variations
     * @param id article number of product
     */
    getProductVariationById(id: string): Product | undefined {
        const selectables = this.getSelectableProducts();

        for (const p of selectables) {
            if (p.id === id) return p;
        }

        let foundProduct: Product | undefined;
        for (const p of selectables) {
            foundProduct = this.getProductVariationById(p.id);
        }

        return foundProduct;
    }

    findNestedVariationById(id: string): Product | undefined {
        if (!this.isVariableProduct()) return this.id === id ? this : undefined;

        let found: Product | undefined;
        for (const v of this.variants) {
            found = v.findNestedVariationById(id);
            if (found) break;
        }

        return found;
    }

    findNestedProductById(id: string): Product | undefined {
        if (this.id === id) return this;

        const productsToSearch = this.isSetProduct() ? this.setProducts : this.variants;

        for (const product of productsToSearch) {
            const foundProduct = product.findNestedProductById(id);
            if (foundProduct) {
                return foundProduct;
            }
        }
    }

    /**
     * Determines if this product is selectable in a simple way.
     * In this case the product selection is handled on the right sidebar within the product details view
     * That means:
     *  - Product has no variants and set products OR
     *  - Product has only 1 layer of variations
     */
    isSimpleProduct(): boolean {
        return !this.isComplexProduct();
    }

    /**
     * Determines if this product selection is complex and needs custom selection
     * In this case the product selection is handled in the bottom of the product details view
     * That means:
     *  - Product has additional custom fields to render
     *  - Product has variations with nested set products OR
     *  - Product has nested variations (multiple layers) OR
     *  - Product has set products (and maybe more nested variations or set products)
     */
    isComplexProduct(): boolean {
        return (
            this.hasVisibleCustomFields() ||
            this.containsVariationsWithNestedSetProducts() ||
            this.containsNestedVariableProducts() ||
            this.isSetProduct()
        );
    }

    /**
     * Determines if this product contains nested variations of a minimum of 2 layers
     */
    containsNestedVariableProducts(): boolean {
        return this.variants.some((product: Product) => product.isVariableProduct());
    }

    /**
     * Determines if this product contains variations with nested set products
     */
    containsVariationsWithNestedSetProducts(): boolean {
        return this.variants.some((product: Product) => product.isSetProduct());
    }

    containsSetProductWithNestedVariations(): boolean {
        return this.setProducts.some((product: Product) => product.isVariableProduct());
    }

    /**
     * Returns an array of products that are selectable for the customer (can be added to the shopping cart)
     */
    getSelectableProducts(): Product[] {
        if (this.isVariableProduct()) return this.variants;
        return [this];
    }

    getEntityDescription(): EntityDescription {
        return EntityDescriptions.REPRESENTATIVE;
    }

    toString(): string {
        return `${this.articleNumber}:${this.localizedName}`;
    }

    toAboSubscriptionOrderProduct(): AboSubscriptionOrderProduct {
        return new AboSubscriptionOrderProduct({
            title: this.localizedName,
            productNumber: this.articleNumber,
            quantity: undefined,
            nettPrice: undefined,
            commissionModelId: undefined,
            deliveries: 0,
            intervalMonth: 1,
            nextBilling: undefined,
            validFrom: DateTime.fromJSDate(new Date(Date.now())).toJSDate(),
            validTo: DateTime.fromJSDate(new Date('4000-01-01 00:00:00')).toJSDate(),
            createdOn: DateTime.fromJSDate(new Date(Date.now())).toJSDate(),
            minRuntime: undefined,
            maxRuntime: undefined,
            subscriptionOrderProductCustomFields: this.customFields.map(field => ({
                fieldName: field.fieldName,
                fieldValue: field.defaultValue ?? '',
            })),
        });
    }

    setDefaultValueForCustomFields(fieldName: string, defaultValue: string) {
        this.customFields
            .filter(customField => customField.fieldName === fieldName)
            .forEach(customField => (customField.defaultValue = defaultValue));
        this.variants.forEach(variant => variant.setDefaultValueForCustomFields(fieldName, defaultValue));
        this.setProducts.forEach(setProduct => setProduct.setDefaultValueForCustomFields(fieldName, defaultValue));
    }

    public calculatePriceAndDiscount(formValues: any, volumeDiscounts: VolumeDiscount[]): CalculatedPriceAndDiscount {
        const [price, discount] = this.calculatePrice(formValues);
        const volumeDiscount = this.calculateVolumeDiscount(formValues, volumeDiscounts, price);
        const [pricePerMonth, priceOfTotalRuntime] = this.getPricePerMonthAndTotal(formValues);
        const currencyIso = this.currencyIso;

        return {
            price,
            discount,
            volumeDiscount,
            pricePerMonth,
            priceOfTotalRuntime,
            currencyIso,
        };
    }

    public getPricePerMonthAndTotal(formValues: any): [number, number] {
        // iterate product form values to calculate sum price
        return formValues.reduce(
            ([pricePerMonth, priceOfTotalRuntime]: [number, number], product: any) => {
                // if the product is a set we have to sum up all parts of the set within the chosen variants
                if (this.isSetProduct() && product.chosenVariants) {
                    return [0, 0];

                    // otherwise the product consists of variations and we multiply the quantity and price of the selected variations
                } else {
                    pricePerMonth +=
                        (product.quantity ? Number(product.quantity) : 0) *
                        (product.pricePerMonth ? Number(product.pricePerMonth) : 0);
                    priceOfTotalRuntime +=
                        (product.quantity ? Number(product.quantity) : 0) *
                        (product.priceOfTotalRuntime ? Number(product.priceOfTotalRuntime) : 0);
                }

                return [pricePerMonth, priceOfTotalRuntime];
            },
            [0, 0]
        );
    }

    /**
     * Calculates the total price of all selected products within the form
     * @param formValues actual form values (selected products with their quantity)
     */
    public calculatePrice(formValues: any): [number, number] {
        let price = 0;
        let discount = 0;

        for (const product of formValues) {
            // Check if we got an empty slot in the array where product would be undefined
            if (product == null) continue;

            if (this.isSetProduct() && product.chosenVariants) {
                for (const chosenVariant of product.chosenVariants.flat()) {
                    const qty = Number(chosenVariant.quantity ?? 1);
                    const pPrice = Number(chosenVariant.price ?? 0);
                    const pDiscount = Number(chosenVariant.discount ?? 0);

                    price += (pPrice - pDiscount) * qty;
                    discount += pDiscount * qty;
                }
            } else {
                const qty = Number(product.quantity ?? 0);
                const pPrice = Number(product.price ?? 0);
                const pDiscount = Number(product.discount ?? 0);

                price += (pPrice - pDiscount) * qty;
                discount += pDiscount * qty;
            }
        }

        return [price, discount];
    }

    /**
     * Calculates the discount for the current given form values.
     * Discounts are not available for set products (only products with variations)
     * We assume that every variation belongs to the same article group!
     * @param formValues actual form values (selected products with their quantity)
     * @param volumeDiscounts volume discounts
     * @param price total sum price of all products selected within the form
     */
    public calculateVolumeDiscount(
        formValues: any[],
        volumeDiscounts: VolumeDiscount[],
        price: number
    ): CalculatedVolumeDiscount | undefined {
        // no volume discounts for set products
        if (this.isSetProduct()) return undefined;

        // get all selected products with quantity from the form
        const productsWithQuantity: any[] = formValues.reduce((result: any[], formProduct) => {
            if (formProduct.quantity > 0) {
                const p = this.getProductVariationById(formProduct.ffProductNumber);
                if (p) {
                    result.push({
                        ...p,
                        quantity: formProduct.quantity,
                    });
                }
            }
            return result;
        }, []);

        // check if there is a potential volume discount available for this article group
        const matchingVolumeDiscount = volumeDiscounts.find(volumeDiscount => {
            if (this.getSelectableProducts().length == 0) return false;
            return (
                volumeDiscount.articleGroupNormalizedName === this.getSelectableProducts()[0].articleGroupNormalizedName
            );
        });

        if (!matchingVolumeDiscount) return undefined;

        // calculate total quantity for discount
        const totalQuantity = productsWithQuantity.reduce((total: number, productWithQuantity: any) => {
            return (
                getTotalQuantity(
                    productWithQuantity.quantity,
                    productWithQuantity.colliEntity,
                    productWithQuantity.colli,
                    matchingVolumeDiscount.baseColli
                ) + total
            );
        }, 0);

        return new CalculatedVolumeDiscount(matchingVolumeDiscount, totalQuantity, price, this.currencyIso);
    }
}
